/** @file A mesh's stale bind (bind pose ≠ setup world) and the rebind a Setup-mode weight edit starts with. */
import { setupWorldOf } from '@pose2d/commands/mesh/setupWorldOf';
import { staleBindBones } from '@pose2d/commands/mesh/staleBindBones';
import type { CommandCall, Doc, Id, MeshAtt } from '@pose2d/format/types';

/**
 * Weights written for a bone whose setup pose changed after the mesh was bound move vertices at the setup pose
 * (W·B⁻¹ ≠ I, walkthrough D6). In Setup mode a weight edit therefore re-captures the bind first (mesh.rebindMesh keeps
 * the setup look) in the same batch, one undo step; Animate mode never rebinds silently (T-473).
 */
export const staleBind = {
  /** The weighted bones of `mesh` whose bind pose differs from their setup world in `doc`; [] for a rigid mesh. */
  bones(doc: Doc, mesh: MeshAtt): Id[] {
    const g = mesh.geometry;
    return g.kind === 'skinned' ? staleBindBones(g.bindPose, setupWorldOf(doc)) : [];
  },
  /** mesh.rebindMesh of `attachment`: the bind re-captured at the current setup pose, the setup look kept. */
  rebind(attachment: Id): CommandCall {
    return { name: 'mesh.rebindMesh', input: { target: attachment } };
  },
  /** `calls` as one batch: in Setup mode on a mesh with a stale bind, mesh.rebindMesh comes first; otherwise unchanged. */
  batch(doc: Doc, target: { attachment: Id; mesh: MeshAtt }, calls: readonly CommandCall[], mode: 'setup' | 'animate'): CommandCall[] {
    const rebind = mode === 'setup' && staleBind.bones(doc, target.mesh).length > 0;
    return rebind ? [staleBind.rebind(target.attachment), ...calls] : [...calls];
  },
};
